import { Skeleton } from '@/components/atoms';
import { Button } from '@/components/atoms/Button';
import { BadgeCheckIcon, BadgeXIcon, ChevronUpIcon, CircleXIcon, ClockFillIcon, CopyIcon, CreditCardIcon, DirhamIcon, InfoIcon, ShieldAlertIcon } from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useUnitInstallments } from '@/modules/auth/hooks/useUnitInstallments';
import { paymentService, PaymentStatusBreakdown, PaymentStatusResponse } from '@/modules/payments/services/paymentService';
import { router } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface PaymentResultScreenProps {
    type: 'success' | 'failed' | 'cancel';
    orderRef?: string;
    onPrimaryActionPress?: (() => void) | undefined;
    onSecondaryActionPress?: (() => void) | undefined;
    onReportIssuePress?: (() => void) | undefined;
    onCopyTransactionIdPress?: ((transactionId: string) => void) | undefined;
}

export default function PaymentResultScreen({
    type: initialType,
    orderRef,
    onPrimaryActionPress,
    onSecondaryActionPress,
    onReportIssuePress,
    onCopyTransactionIdPress,
}: PaymentResultScreenProps) {
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();
    const { fetchAllUnitInstallments } = useUnitInstallments();
    const [isPaymentDetailsExpanded, setIsPaymentDetailsExpanded] = useState(false);
    const [type, setType] = useState<'success' | 'failed' | 'cancel'>(initialType);
    const [isPolling, setIsPolling] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState<PaymentStatusResponse | null>(null);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isPollingRef = useRef(false);
    const hasRefreshedRef = useRef(false); // Track if we've already refreshed installments
    const isSavingTxRef = useRef(false);
    const lastTrackedStatusRef = useRef<string | null>(null);

    const isSuccess = type === 'success';
    const isCancel = type === 'cancel';
    const isFailed = type === 'failed';

    const PAYMENT_TX_MAP_KEY = 'payment.transactions';

    const persistTransactionIds = async (txId: string | null | undefined, breakdown?: PaymentStatusBreakdown[]) => {
        if (!txId || !breakdown || breakdown.length === 0) return;
        if (isSavingTxRef.current) return;
        isSavingTxRef.current = true;
        try {
            const raw = (await asyncStorage.getItem(PAYMENT_TX_MAP_KEY)) || '{}';
            const map = JSON.parse(raw) as Record<string, { transactionId: string; orderRef?: string | undefined; updatedAt: number }>;
            breakdown.forEach((item) => {
                if (item?.id) {
                    map[item.id] = { transactionId: txId, orderRef: paymentStatus?.orderRef || undefined, updatedAt: Date.now() };
                }
            });
            await asyncStorage.setItem(PAYMENT_TX_MAP_KEY, JSON.stringify(map));
            logger.info('PaymentResultScreen', 'Persisted transaction ids for installments', {
                count: breakdown.length,
                transactionId: txId,
            });
        } catch (error) {
            logger.warn('PaymentResultScreen', 'Failed to persist transaction ids', { error });
        } finally {
            isSavingTxRef.current = false;
        }
    };

    /**
     * Fetch payment status - always fetch if orderRef is available
     */
    useEffect(() => {
        // Always fetch payment status if we have orderRef, regardless of initial type
        if (!orderRef) {
            return;
        }

        // Fetch payment status
        const fetchPaymentStatus = async () => {
            if (isPollingRef.current) {
                return;
            }

            isPollingRef.current = true;
            setIsPolling(true);

            const pollStatus = async () => {
                try {
                    const status = await paymentService.getPaymentStatus({ orderRef });
                    logger.debug('PaymentResultScreen:Payment status fetched', status);
                    setPaymentStatus(status);

                    // Update type based on status
                    if (status.status === 'SUCCESS') {
                        // Persist transaction id mapping for breakdown items
                        await persistTransactionIds(status.transaction?.id, status.breakdown);

                        setType('success');
                        setIsPolling(false);
                        isPollingRef.current = false;
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        
                        // Clear localStorage on web after successful payment (orderRef no longer needed)
                        if (typeof window !== 'undefined') {
                            try {
                                localStorage.removeItem('payment.orderRef');
                            } catch (error) {
                                // Silently fail if localStorage is not available
                            }
                        }
                        // Refresh unit installments in background ONLY after status API confirms SUCCESS
                        // This ensures we only refresh after payment is actually confirmed successful
                        if (!hasRefreshedRef.current) {
                            hasRefreshedRef.current = true;
                            logger.info('PaymentResultScreen', 'Payment status confirmed SUCCESS - refreshing unit installments in background');
                            
                            // Fetch in background (non-blocking) - don't await
                            // If user navigates away, the refresh will continue in background and update store when complete
                            fetchAllUnitInstallments()
                                .then(() => {
                                    logger.info('PaymentResultScreen', 'Unit installments refreshed successfully after payment');
                                })
                                .catch((error) => {
                                    logger.error('PaymentResultScreen', 'Failed to refresh unit installments after payment success', error);
                                    // Silently fail - don't show error to user, data will refresh on next home visit
                                });
                        }
                    } else if (status.status === 'FAILED' || status.status === 'DECLINED') {
                        setType('failed');
                        setIsPolling(false);
                        isPollingRef.current = false;
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        
                        // Clear localStorage on web after failed payment (orderRef no longer needed)
                        if (typeof window !== 'undefined') {
                            try {
                                localStorage.removeItem('payment.orderRef');
                            } catch (error) {
                                // Silently fail if localStorage is not available
                            }
                        }
                    } else if (status.status === 'CANCELLED') {
                        setType('cancel');
                        setIsPolling(false);
                        isPollingRef.current = false;
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        
                        // Clear localStorage on web after cancelled payment (orderRef no longer needed)
                        if (typeof window !== 'undefined') {
                            try {
                                localStorage.removeItem('payment.orderRef');
                            } catch (error) {
                                // Silently fail if localStorage is not available
                            }
                        }
                    } else if (status.status === 'PENDING') {
                        // If status is PENDING, continue polling
                        // Reset polling ref so next poll can run
                        isPollingRef.current = false;
                        // Keep isPolling true to show loading state in UI
                        setIsPolling(true);
                        logger.debug('PaymentResultScreen:Payment status is PENDING, continuing to poll', {
                            transactionId: status.transaction?.id,
                            orderRef: status.orderRef,
                        });
                    }
                } catch (error) {
                    logger.error('PaymentResultScreen:Failed to fetch payment status', error);
                    // On error, stop polling but keep the initial type
                    setIsPolling(false);
                    isPollingRef.current = false;
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                    }
                }
            };

            // Fetch immediately
            await pollStatus();

            // If status is PENDING, poll every 3 seconds
            // Only start polling interval if status might change
            if (initialType !== 'failed' && initialType !== 'cancel') {
                pollingIntervalRef.current = setInterval(pollStatus, 3000);
            }
        };

        void fetchPaymentStatus();

        // Cleanup on unmount
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            isPollingRef.current = false;
            // Note: fetchAllUnitInstallments will continue in background even if user navigates away
            // This is desired behavior - data will be updated in store when refresh completes
        };
    }, [orderRef, initialType, fetchAllUnitInstallments]);

    // Track Dynatrace events for final payment result states
    useEffect(() => {
        if (!paymentStatus) return;

        const { status, orderRef: statusOrderRef, totalAmount, transaction, error } = paymentStatus;
        if (!status) return;

        // Avoid sending duplicate events for the same status
        if (lastTrackedStatusRef.current === status) {
            return;
        }

        const commonPayload: Record<string, string | number | boolean | undefined> = {
            p_screen: 'Payment Result',
            p_sectionName: 'Payment Flow',
            orderRef: statusOrderRef,
            totalAmount: parseFloat(totalAmount),
            transactionId: transaction?.id || '',
            paymentStatus: status,
        };

        let failedMessage: string | undefined;
        if (status === 'FAILED' || status === 'DECLINED') {
            if (typeof error === 'object' && error !== null) {
                failedMessage = (error as { message?: string; code?: string }).message
                    || (error as { message?: string; code?: string }).code;
            } else if (typeof error === 'string') {
                failedMessage = error;
            }
        }

        if (status === 'SUCCESS') {
            trackDynatraceEvent({
                ...commonPayload,
                p_actionName: 'Payment Result Success',
                eventType: 'action',
            });
        } else if (status === 'FAILED' || status === 'DECLINED') {
            trackDynatraceEvent({
                ...commonPayload,
                p_actionName: 'Payment Result Failed',
                eventType: 'error',
                p_failedMessage: failedMessage,
            });
        } else if (status === 'CANCELLED') {
            trackDynatraceEvent({
                ...commonPayload,
                p_actionName: 'Payment Result Cancelled',
                eventType: 'action',
            });
        } else if (status === 'PENDING') {
            trackDynatraceEvent({
                ...commonPayload,
                p_actionName: 'Payment Result Pending',
                eventType: 'action',
            });
        }

        lastTrackedStatusRef.current = status;
    }, [paymentStatus]);

    const handlePrimaryActionPress = () => {
        trackCatalogEvent({
            screen: DT_SCREEN.PAYMENT_RESULT,
            actionName: 'payment_result_primary_action',
            eventType: 'action',
        });
        if (onPrimaryActionPress) {
            onPrimaryActionPress();
            return;
        }

        if (isSuccess) {
            logger.info('PaymentResult', t('payments.backToHome'));
            // Clear navigation stack before navigating to home
            try {
                if (router.canGoBack()) {
                    router.dismissAll();
                }
            } catch (error) {
                // Ignore dismissAll errors - replace will handle navigation
                logger.debug('PaymentResultScreen', 'dismissAll failed, using replace only', error);
            }
            router.replace('/(auth)/(home)' as any);
            return;
        }

        if (isCancel) {
            logger.info('PaymentResult', t('payments.tryAgainPressed'));
        } else {
            logger.info('PaymentResult', t('payments.retryPressed'));
        }
    };

    const handleSecondaryActionPress = () => {
        trackCatalogEvent({
            screen: DT_SCREEN.PAYMENT_RESULT,
            actionName: 'payment_result_secondary_action',
            eventType: 'action',
        });
        if (onSecondaryActionPress) {
            onSecondaryActionPress();
        } else {
            // Clear navigation stack before navigating to home
            try {
                if (router.canGoBack()) {
                    router.dismissAll();
                }
            } catch (error) {
                // Ignore dismissAll errors - replace will handle navigation
                logger.debug('PaymentResultScreen', 'dismissAll failed, using replace only', error);
            }
            router.replace('/(auth)/(home)' as any);
        }
    };

    const handleReportIssuePress = () => {
        trackCatalogEvent({
            screen: DT_SCREEN.PAYMENT_RESULT,
            actionName: 'payment_result_report_issue',
            eventType: 'action',
        });
        if (onReportIssuePress) {
            onReportIssuePress();
        } else {
            logger.info('PaymentResult', t('payments.reportIssuePressed'));
        }
    };

    const handleCopyTransactionIdPress = () => {
        trackCatalogEvent({
            screen: DT_SCREEN.PAYMENT_RESULT,
            actionName: 'payment_result_copy_transaction_id',
            eventType: 'action',
        });
        // Only copy actual transaction ID when available
        const idToCopy = paymentStatus?.transaction?.id || '';
        if (onCopyTransactionIdPress && idToCopy) {
            onCopyTransactionIdPress(idToCopy);
        } else {
            logger.info('PaymentResult', t('payments.transactionIdCopied'));
        }
    };

    const togglePaymentDetails = () => {
        setIsPaymentDetailsExpanded(!isPaymentDetailsExpanded);
    };

    // Use payment status data from API
    const paymentData = paymentStatus
        ? {
            totalAmount: parseFloat(paymentStatus.totalAmount),
            items: paymentStatus.breakdown.map((item: PaymentStatusBreakdown) => ({
                name: item.payment_type === 'installment' 
                    ? 'Installment' 
                    : item.payment_type === 'other_charge' 
                        ? 'Other charge' 
                        : t('payments.convenienceFee'),
                amount: item.value,
            })),
        }
        : null; // No mock data - show loading or empty state

    // Only use transaction ID from payment status; if missing, do not show
    const transactionId = paymentStatus?.transaction?.id || null;
    
    // Check if payment status is PENDING
    const isPendingStatus = paymentStatus?.status === 'PENDING';

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            {/* Status Bar Spacing */}
            <View style={{ height: insets.top }} />

            <ScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                bounces={true}
                alwaysBounceVertical={false}
            >
                <View style={styles.content}>
                    {/* Main Illustration and Message */}
                    <View style={styles.mainSection}>
                        {/* Credit Card with Badge Illustration */}
                        <View style={styles.illustrationContainer}>
                            <CreditCardIcon size={77} />
                            <View style={styles.badgeContainer}>
                                {isPendingStatus ? (
                                    <ClockFillIcon size={30} color={UI_PRIMITIVES.yellowDark} />
                                ) : isSuccess ? (
                                    <BadgeCheckIcon size={30} />
                                ) : isCancel ? (
                                    <InfoIcon size={30} color={colors.fontPrimary} />
                                ) : (
                                    <BadgeXIcon size={30} />
                                )}
                            </View>
                        </View>

                        <Typography variant="largeTitle" color="primary" style={styles.title}>
                            {isPendingStatus
                                ? t('payments.paymentPendingTitle')
                                : isSuccess
                                    ? t('payments.paymentSuccessTitle')
                                    : isCancel
                                        ? t('payments.paymentCancelledTitle')
                                        : t('payments.paymentFailedTitle')}
                        </Typography>

                        <Typography variant="body" color="primary" style={styles.description}>
                            {isPendingStatus || isSuccess
                                ? t('payments.paymentSuccessDescription')
                                : isCancel
                                    ? t('payments.paymentCancelledDescription')
                                    : t('payments.paymentFailedDescription')}
                        </Typography>
                    </View>

                    {/* Status Message */}
                    {isPendingStatus ? (
                        // Show pending state with skeleton and notification message
                        <View style={styles.transactionIdContainer}>
                            <Skeleton width={200} height={20} borderRadius={4} />
                            <Skeleton width={24} height={24} borderRadius={4} style={{ marginLeft: 10 }} />
                        </View>
                    ) : isSuccess ? (
                        transactionId ? (
                            <View style={styles.transactionIdContainer}>
                                <Typography variant="subhead" color="primary" style={styles.transactionIdText}>
                                    {t('payments.transactionId')}: {transactionId}
                                </Typography>
                                <Button
                                    title=""
                                    variant="text"
                                    onPress={handleCopyTransactionIdPress}
                                    style={styles.copyButton}
                                    trailing={<CopyIcon size={20} color={colors.fontPrimary} />}
                                />
                            </View>
                        ) : (
                            // Show skeleton while waiting for transaction ID from payment status
                            <View style={styles.transactionIdContainer}>
                                <Skeleton width={200} height={20} borderRadius={4} />
                                <Skeleton width={24} height={24} borderRadius={4} style={{ marginLeft: 10 }} />
                            </View>
                        )
                    ) : isFailed ? (
                        paymentStatus?.error ? (
                            <View style={styles.errorStatusContainer}>
                                <CircleXIcon size={12} color={colors.fontErrorText} />
                                <Typography variant="footnote" color="errorText" style={[styles.errorMessage, { color: colors.fontErrorText }]}>
                                    {(() => {
                                        // Handle error as object or string
                                        if (typeof paymentStatus.error === 'object' && paymentStatus.error !== null) {
                                            return paymentStatus.error.message || paymentStatus.error.code || t('payments.paymentErrorCode');
                                        }
                                        return paymentStatus.error;
                                    })()}
                                </Typography>
                            </View>
                        ) : (
                            <View style={styles.errorStatusContainer}>
                                <Skeleton width={12} height={12} borderRadius={6} />
                                <Skeleton width={180} height={16} borderRadius={4} style={{ marginLeft: 2 }} />
                            </View>
                        )
                    ) : isPolling || !paymentStatus ? (
                        // Show skeleton while polling or when payment status is not yet loaded
                        isSuccess ? (
                            <View style={styles.transactionIdContainer}>
                                <Skeleton width={200} height={20} borderRadius={4} />
                                <Skeleton width={24} height={24} borderRadius={4} style={{ marginLeft: 10 }} />
                            </View>
                        ) : (
                            <View style={styles.errorStatusContainer}>
                                <Skeleton width={12} height={12} borderRadius={6} />
                                <Skeleton width={180} height={16} borderRadius={4} style={{ marginLeft: 2 }} />
                            </View>
                        )
                    ) : null}

                    {/* Pending Status Notification Message */}
                    {isPendingStatus && (
                        <View style={[styles.infoNotice, { backgroundColor: colors.backgroundLevel2 }]}>
                            <InfoIcon size={24} color={colors.fontPrimary} />
                            <Typography variant="subhead" color="primary" style={styles.infoNoticeText}>
                                {t('payments.pendingPaymentNotification')}
                            </Typography>
                        </View>
                    )}

                    {/* Info Notice (Success only) */}
                    {isSuccess && !isPendingStatus && (
                        <View style={[styles.infoNotice, { backgroundColor: colors.backgroundLevel2 }]}>
                            <InfoIcon size={24} color={colors.fontPrimary} />
                            <Typography variant="subhead" color="primary" style={styles.infoNoticeText}>
                                {t('payments.chequeNotice')}
                            </Typography>
                        </View>
                    )}

                    {/* Payment Details Section - Only show if payment data is available */}
                    {paymentData && (
                        <View style={styles.paymentDetailsSection}>
                            <Typography variant="headline" color="primary" style={styles.paymentDetailsTitle}>
                                {t('payments.paymentDetails')}
                            </Typography>

                            <TouchableOpacity
                                style={[styles.paymentDetailsCard, { borderColor: colors.outlineInactive }]}
                                onPress={togglePaymentDetails}
                                activeOpacity={0.7}
                            >
                                {/* Total Amount Row */}
                                <View style={styles.totalAmountRow}>
                                    <Typography variant="headline" color="primary" style={styles.totalAmountLabel}>
                                        {t('payments.totalAmount')}
                                    </Typography>
                                    <View style={[styles.totalAmountContainer, { direction: 'ltr' }]}>
                                        <DirhamIcon size={15} color={colors.fontPrimary} />
                                        <Typography variant="headline" color="primary" style={styles.totalAmountValue}>
                                            {paymentData.totalAmount.toLocaleString()}
                                        </Typography>
                                    </View>
                                </View>

                                {/* Expandable Payment Items */}
                                {isPaymentDetailsExpanded && (
                                    <View style={styles.paymentItemsContainer}>
                                        {paymentData.items.map((item: { name: string; amount: number }, index: number) => (
                                            <View key={index} style={styles.paymentItemRow}>
                                                <Typography variant="subhead" color="primary" style={styles.paymentItemName}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="body" color="primary" style={styles.paymentItemAmount}>
                                                    {item.amount.toLocaleString()}
                                                </Typography>
                                            </View>
                                        ))}
                                    </View>
                                )}

                                {/* Expand/Collapse Arrow */}
                                <View style={styles.expandArrowContainer}>
                                    <View style={{ transform: [{ rotate: isPaymentDetailsExpanded ? '0deg' : '180deg' }] }}>
                                        <ChevronUpIcon
                                            size={24}
                                            color={colors.fontPrimary}
                                        />
                                    </View>
                                </View>
                            </TouchableOpacity>
                        </View>
                    )}

                    {/* Report Issue Button */}
                    <Button
                        title={t('payments.reportIssue')}
                        variant="text"
                        onPress={handleReportIssuePress}
                        style={styles.reportIssueButton}
                        trailing={<ShieldAlertIcon size={18} color={colors.fontPrimary} />}
                    />

                    {/* Extra spacing to ensure report issue button is visible */}
                    <View style={styles.bottomSpacer} />
                </View>
            </ScrollView>

            {/* Bottom Action Buttons */}
            <View style={[styles.bottomSection, { paddingBottom: insets.bottom + 12, backgroundColor: colors.backgroundLevel1 }]}>
                <Button
                    title={
                        isPendingStatus || isSuccess
                            ? t('payments.backToHome')
                            : isCancel
                                ? t('payments.tryAgain')
                                : t('payments.retry')
                    }
                    variant="primary"
                    onPress={handlePrimaryActionPress}
                    style={styles.primaryButton}
                />
                {!isSuccess && !isPendingStatus && (
                <Button
                    title={t('payments.backToHome')}
                    variant="text"
                    onPress={handleSecondaryActionPress}
                    style={styles.secondaryButton}
                />
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
        paddingBottom: 150, // Increased space for bottom buttons when content is expanded
    },
    content: {
        paddingHorizontal: 16,
        paddingTop: 64, // Empty space after status bar (matches Figma top-[114px] - 50px status bar)
        gap: 16,
        alignItems: 'center',
    },
    mainSection: {
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 24,
        paddingVertical: 24,
    },
    illustrationContainer: {
        width: 88,
        height: 88,
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },
    badgeContainer: {
        position: 'absolute',
        left: 50,
        top: 39,
    },
    title: {
        textAlign: 'center',
        fontSize: 28,
        fontWeight: '500',
    },
    description: {
        textAlign: 'center',
        fontSize: 15,
        fontWeight: '400',
        lineHeight: 24,
    },
    transactionIdContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        justifyContent: 'center',
    },
    transactionIdText: {
        fontSize: 14,
        fontWeight: '500',
    },
    copyButton: {
        paddingVertical: 0,
        paddingHorizontal: 0,
    },
    errorStatusContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        justifyContent: 'center',
    },
    errorMessage: {
        fontSize: 13,
        fontWeight: '400',
    },
    infoNotice: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        borderRadius: 12,
        width: '100%',
    },
    infoNoticeText: {
        flex: 1,
        fontSize: 14,
        fontWeight: '500',
        lineHeight: 20,
    },
    paymentDetailsSection: {
        width: '100%',
        gap: 10,
        alignItems: 'flex-start',
    },
    paymentDetailsTitle: {
        fontSize: 16,
        fontWeight: '600',
    },
    paymentDetailsCard: {
        borderWidth: 1,
        borderRadius: 12,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 4,
        width: '100%',
        gap: 8,
    },
    totalAmountRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    totalAmountLabel: {
        flex: 1,
        fontSize: 16,
        fontWeight: '600',
    },
    totalAmountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    totalAmountValue: {
        fontSize: 16,
        fontWeight: '600',
    },
    paymentItemsContainer: {
        gap: 8,
        width: '100%',
    },
    paymentItemRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    paymentItemName: {
        fontSize: 14,
        fontWeight: '500',
    },
    paymentItemAmount: {
        fontSize: 16,
        fontWeight: '400',
        textAlign: 'right',
    },
    expandArrowContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: 4, // Match Figma pb-[4px]
        paddingTop: 0, // No top padding
    },
    reportIssueButton: {
        paddingVertical: 12,
        paddingHorizontal: 12,
        borderRadius: 4,
        alignSelf: 'center',
        width: 329,
        height: 48,
    },
    bottomSpacer: {
        height: 20, // Extra spacing to ensure report issue button is visible above bottom buttons
    },
    bottomSection: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        paddingHorizontal: 16,
        paddingTop: 12,
        gap: 12,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: -4,
        },
        shadowOpacity: 0.25,
        shadowRadius: 16,
        elevation: 8,
    },
    primaryButton: {
        width: '100%',
    },
    secondaryButton: {
        width: '100%',
    },
    pollingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        justifyContent: 'center',
    },
    pollingText: {
        fontSize: 14,
        fontWeight: '500',
    },
});
